import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { Link } from 'react-router-dom'
import { formatAddressLine } from '../addressUtils'
import type { ConsolidatedLocation } from '../mockLocations'
import RowOverflowMenu from './RowOverflowMenu'
import { LocationStatusBadge, StatusBadge } from './StatusBadges'

const t = buildTranslate('locations.location_list')

type Props = {
  location: ConsolidatedLocation
  selected: boolean
  onToggle: (id: number) => void
  onToggleOrdering: (location: ConsolidatedLocation) => void
  onViewDetails: (location: ConsolidatedLocation) => void
  rowMutationLoading: boolean
  showOrderingControls: boolean
}

const LocationRow = ({
  location,
  selected,
  onToggle,
  onToggleOrdering,
  onViewDetails,
  rowMutationLoading,
  showOrderingControls,
}: Props) => {
  const [css] = useStyletron()
  const cellClass = `align-middle ${css({
    backgroundColor: palette.grey05,
    borderBottom: `1px solid ${palette.grey30}`,
    padding: '20px 24px',
  })}`

  return (
    <tr>
      {showOrderingControls && (
        <td className={`${cellClass} ${css({ width: '68px' })}`}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(location.id)}
            aria-label={t('row_checkbox_label', { name: location.name })}
          />
        </td>
      )}
      <td className={cellClass}>
        <div className={`bold ${css({ color: palette.grey90 })}`}>
          {location.name}
        </div>
        <div className={`body-text-4 ${css({ color: palette.grey70 })}`}>
          {/* "street, city, state zip" — the conventional US address
              line order, with missing pieces compacted so partial
              addresses don't render dangling commas. */}
          {formatAddressLine(location)}
        </div>
      </td>
      <td className={`${cellClass} ${css({ width: '203px' })}`}>
        <LocationStatusBadge status={location.locationStatus} />
      </td>
      {showOrderingControls && (
        <td className={`${cellClass} ${css({ width: '191px' })}`}>
          <StatusBadge enabled={location.orderingEnabled} />
        </td>
      )}
      <td className={`${cellClass} ${css({ width: '82px' })}`}>
        <Link
          to={`/locations/${location.id}`}
          aria-label={t('table.view_aria', { name: location.name })}
          className={`body-text-4 ${css({
            color: palette.belizeHole,
          })}`}
        >
          {t('table.view')}
        </Link>
      </td>
      {showOrderingControls && (
        <td className={`${cellClass} ${css({ width: '80px' })}`}>
          <RowOverflowMenu
            location={location}
            onToggleOrdering={onToggleOrdering}
            onViewDetails={onViewDetails}
            disabled={rowMutationLoading}
          />
        </td>
      )}
    </tr>
  )
}

export default LocationRow
